import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { ArrowLeft, Loader2, PartyPopper } from "lucide-react";
import { emptyRisorse } from "../components/evento-form/fields";
import { EngagementCard } from "../components/evento-form/EngagementCard";
import { SendModeCard } from "../components/request-form/SendModeCard";
import { DatiGeneraliSection, DatiOperativiSection, ContabilitaEventoSection } from "../components/evento-form/EventoSections";
import { useEventoPrefill } from "../components/evento-form/useEventoPrefill";
import { useAuth } from "../context/AuthContext";

const EMPTY_EVENTO = {
  denominazione: "", tipo: "", luogo: "", data_ora_inizio: "", data_ora_fine: "",
  risorse: emptyRisorse(), luogo_stazionamento: "", referente: "", telefono_referente: "", referente_nexus: "",
};
const EMPTY_CONTABILITA = { importo: "", importo_totale: "", incasso: "", modalita_pagamento: "", fee_nexus: "", codice_fiscale: "", note: "" };

export default function CreateEvento() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const duplicaId = searchParams.get("duplica");
  const preventivoId = searchParams.get("preventivo");
  const isEdit = Boolean(id);
  const [dupCode, setDupCode] = useState("");
  const [editCode, setEditCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("fcfs");
  const [assigned, setAssigned] = useState(false);
  const [partners, setPartners] = useState([]);
  const [directPartnerId, setDirectPartnerId] = useState("");
  const [sendMode, setSendMode] = useState("all");
  const [sendIds, setSendIds] = useState([]);
  const [sendListIds, setSendListIds] = useState([]);
  const [richiedente, setRichiedente] = useState("");
  const [richiedenti, setRichiedenti] = useState([]);
  const [evento, setEvento] = useState({ ...EMPTY_EVENTO, referente_nexus: (!id && user?.operatore_co) || "" });
  const [contabilita, setContabilita] = useState(EMPTY_CONTABILITA);

  useEffect(() => {
    if (!isEdit) api.get("/partners").then(({ data }) => setPartners(data)).catch(() => {});
    api.get("/richiedenti").then(({ data }) => setRichiedenti(data)).catch(() => {});
  }, [isEdit]);

  useEventoPrefill({ id, duplicaId, preventivoId, isEdit, setters: { setEditCode, setMode, setDupCode, setAssigned, setEvento, setContabilita, setRichiedente } });

  const richiedenteOptions = useMemo(
    () => [...new Set([...richiedenti.map((r) => r.nome), ...(richiedente ? [richiedente] : [])])], [richiedenti, richiedente]);
  const setEv = (k, v) => setEvento((e) => ({ ...e, [k]: v }));
  const setRis = (key, patch) => setEvento((e) => ({ ...e, risorse: { ...e.risorse, [key]: { ...e.risorse[key], ...patch } } }));
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/operativa"));

  const submit = async () => {
    if (!isEdit && mode === "direct" && !directPartnerId) return toast.error("Seleziona il partner per l'assegnazione diretta");
    if (!isEdit && mode !== "direct" && sendMode === "lista" && !sendIds.length) return toast.error("Seleziona almeno un partner o una lista a cui inviare l'evento");
    setLoading(true);
    try {
      const sendPayload = !isEdit && mode !== "direct" && sendMode === "lista" ? { send_mode: "lista", partner_ids: sendIds, list_ids: sendListIds } : {};
      const payload = { engagement_mode: mode, evento, contabilita, richiedente, ...sendPayload, ...(mode === "direct" && !isEdit ? { direct_partner_id: directPartnerId } : {}) };
      const { data } = isEdit ? await api.put(`/eventi/${id}`, payload) : await api.post("/eventi", payload);
      toast.success(isEdit ? `Evento ${data.code} aggiornato` : `Evento ${data.code} pubblicato`, isEdit ? undefined : { description: "I partner sono stati notificati via email." });
      navigate(`/operativa/richiesta/${data.id}`);
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  return (
    <AppLayout crumbLabel={editCode}>
      <div className="max-w-4xl mx-auto nx-fade-in pb-10">
        <Button variant="ghost" size="sm" className="gap-1 mb-3 -ml-2" onClick={goBack}><ArrowLeft className="h-4 w-4" /> Indietro</Button>
        <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1 flex items-center gap-2">
          <PartyPopper className="h-7 w-7 text-primary" /> {isEdit ? "Modifica Evento" : dupCode ? `Duplica ${dupCode} — Nuovo Evento` : "Nuovo Evento / Manifestazione"}
        </h1>
        <p className="text-sm text-slate-500 mb-6">{isEdit ? "Aggiorna i dati dell'evento" : "Compila le sezioni per pubblicare l'evento ai Partner"}</p>

        <EngagementCard mode={mode} setMode={setMode} disabled={assigned} isEdit={isEdit} partners={partners} directPartnerId={directPartnerId} setDirectPartnerId={setDirectPartnerId} />
        {!isEdit && mode !== "direct" && <SendModeCard testPrefix="ev-send" sendMode={sendMode} setSendMode={setSendMode} partners={partners} selectedIds={sendIds} setSelectedIds={setSendIds} selectedListIds={sendListIds} setSelectedListIds={setSendListIds} />}

        <div className="space-y-5">
          <DatiGeneraliSection evento={evento} setEv={setEv} richiedente={richiedente} setRichiedente={setRichiedente} richiedenteOptions={richiedenteOptions} />
          <DatiOperativiSection evento={evento} setEv={setEv} setRis={setRis} />
          <ContabilitaEventoSection contabilita={contabilita} setContabilita={setContabilita} />
        </div>

        <Separator className="my-6" />
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={goBack}>Annulla</Button>
          <Button data-testid="submit-evento" onClick={submit} disabled={loading} className="gap-2 min-w-[180px]">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Salva Modifiche" : "Pubblica Evento")}
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
